import { RESET_COMPLETE_GRAPH } from 'store/core/constants';
import {
  REMOVE_GRAPH_ITEMS,
  SET_GRAPH_ITEMS,
  SET_IS_LOADING,
  SET_SELECTED_LINK,
  SET_SELECTED_VERTEX,
  SET_TOP_CONNECTIONS,
  SET_TOP_CONNECTIONS_LOADING,
  SET_SEARCH_CONTEXT,
  SET_MOST_CONNECTED,
  SET_MOST_CONNECTED_LOADING,
  SET_FOCUSED_NODE,
  UPDATE_SEARCH_CONTEXT_DISPLAY,
  GET_RELATIONSHIP_SUGGESTIONS,
  SET_RELATIONSHIP_SUGGESTIONS,
  SET_RELATIONSHIP_SUGGESTIONS_LOADING,
  DISMISS_RELATIONSHIP_SUGGESTION,
  CONFIRM_RELATIONSHIP_SUGGESTION,
  SET_ACTIVE_SUGGESTION_PANEL,
  SET_SELECTED_SUGGESTED_EDGE,
  CLEAR_SUGGESTIONS,
  CLEAR_SUGGESTIONS_FOR_SOURCE,
} from './constants';
import { getNextMinTraversal } from 'utils/query-helper';
import { GetBaseNodeStyles } from 'utils/graph-helper';
import { REMOVE_NL_MESSAGE, SET_NL_MESSAGE } from 'store/nl/actions';
import { perfMark, perfMeasure, perfCount } from 'utils/graph-perf';

const oldTraversalBeforeContext = 'oldTraversalBeforeContext';

export const initialState = {
  nodes: {}, // Map<nodeId, {node, contextids: {contextid: degreeOfSeparation}}>
  links: {}, // Map<linkId, {link, contextids: {contextid: contextId}}>
  contexts: {}, // Map<contextId, {value, nodes: [contextItemIds]}>

  graphItems: {},
  contextIds: {},
  topConnections: {},
  topConnectionsLoading: false,
  mostConnected: {},
  mostConnectedLoading: false,

  searchContext: {},
  aiContext: [],
  selectedVertex: undefined,
  selectedEdgeId: undefined,
  isLoading: false,
  focusedNode: null,
  focusPingTick: 0,

  suggestedNodes: {},
  suggestedEdges: {},
  dismissedSuggestions: [],
  activeSuggestionPanel: null,
  selectedSuggestedEdge: null,
  suggestionsLoading: false,
  confirmationResult: null,
};

export const graphReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_GRAPH_ITEMS: {
      const { context, newNodes, newLinks, nodeCount } = action;
      
      const { nodes, links, contexts, contextIds } = state;

      if (nodeCount === 0) {
        const updatedContexts = { ...contexts };
        updatedContexts[context.id] = {
          value: { ...context, nodeCount, id: context.id },
          nodeIds: [],
          linkIds: [],
        };

        return { ...state, contexts: updatedContexts };
      }

      //set New contextId based on type
      let newContextId = context.id;
      const isRegionSearch =
        (context.metroRegion && !context.id) ||
        context.metroRegion === context.id;
      if (isRegionSearch) {
        newContextId = context.metroRegion;
      }

      // set graph nodes
      let updatedNodes = { ...nodes };
      const contextNodeIds = [];

      for (const nodeId in newNodes) {
        contextNodeIds.push(nodeId);
        const item = newNodes[nodeId];
        if (updatedNodes[nodeId]) {
          const currentNode = updatedNodes[nodeId];
          let localContextIds = { ...currentNode.contextIds };
          localContextIds[newContextId] = item.traversal;

          let nodeToAdd = currentNode.node;
          let traversal = currentNode.node?.traversal;
          if (nodeToAdd.traversal > item.traversal) {
            traversal = item?.traversal;
          }

          if (item.isContextNode) {
            const oldValue = currentNode.node.traversal;
            nodeToAdd = item;
            nodeToAdd[oldTraversalBeforeContext] = oldValue;
          }
          nodeToAdd.traversal = traversal;

          updatedNodes[nodeId] = {
            node: nodeToAdd,
            contextIds: localContextIds,
          };
        } else {
          let localContextIds = {};
          localContextIds[newContextId] = item.traversal;
          updatedNodes[nodeId] = { node: item, contextIds: localContextIds };
        }
      }

      // set graph links
      const contextLinkIds = [];
      let updatedLinks = { ...links };
      for (const linkId in newLinks) {
        contextLinkIds.push(linkId);
        const link = newLinks[linkId];
        const currentLink = updatedLinks[linkId];
        if (updatedLinks[linkId]) {
          let localContextIds = { ...currentLink.contextIds };
          localContextIds[newContextId] = newContextId;
          updatedLinks[linkId] = {
            link: currentLink.link,
            contextIds: localContextIds,
          };
        } else {
          let localContextIds = {};
          localContextIds[newContextId] = newContextId;
          updatedLinks[linkId] = { link: link, contextIds: localContextIds };
        }
      }

      // set context
      const updatedContexts = { ...contexts };
      updatedContexts[newContextId] = {
        value: { ...context, nodeCount, id: newContextId },
        nodeIds: contextNodeIds,
        linkIds: contextLinkIds,
      };

      const updatedContextIds = { ...contextIds };
      if (!isRegionSearch) {
        updatedContextIds[newContextId] = newContextId;
      }

      return {
        ...state,
        nodes: updatedNodes,
        links: updatedLinks,
        contexts: updatedContexts,
        contextIds: updatedContextIds,
        searchContext: {},
      };
    }
    case REMOVE_GRAPH_ITEMS: {
      const { contextId } = action;
      const {
        nodes,
        links,
        contexts,
        selectedVertex,
        selectedEdgeId,
        contextIds,
      } = state;
      
      // For region contexts, contextId might be the metroRegion; ensure we have valid context
      const contextToRemove = contexts[contextId];
      if (!contextToRemove) {
        // Context not found, return unchanged state
        return state;
      }
      
      const nodeIdsToDelete = contextToRemove.nodeIds;
      const linkIdsToDelete = contextToRemove.linkIds;

      let currentNodes = { ...nodes };
      let currentLinks = { ...links };
      let updatedContexts = { ...contexts };
      let updatedContextIds = { ...contextIds };
      let updatedSelectedVertexId = selectedVertex;
      let updatedSelectedEdgeId = selectedEdgeId;

      // delete node
      for (const index in nodeIdsToDelete) {
        const id = nodeIdsToDelete[index];
        let nodeToDelete = currentNodes[id];
        if (nodeToDelete) {
          if (Object.keys(nodeToDelete.contextIds).length > 1) {
            delete nodeToDelete.contextIds[contextId];
            const nextTraversal = getNextMinTraversal(
              nodeToDelete.contextIds,
              contextId,
            );
            if (nodeToDelete?.node?.data?.nodeKey === contextId) {
              const baseStyles = GetBaseNodeStyles(
                nodeToDelete.node,
                state.nodeSizingType,
              );
              const updatedStyledNode = {
                ...nodeToDelete.node,
                ...baseStyles,
                traversal: nodeToDelete.node[oldTraversalBeforeContext],
                isContextNode: false,
              };
              currentNodes[contextId] = {
                node: { ...updatedStyledNode },
                contextIds: nodeToDelete.contextIds,
              };
            } else {
              //TODO: reset traversal based on remaining context
              currentNodes[id] = {
                node: { ...nodeToDelete.node, traversal: nextTraversal },
                contextIds: nodeToDelete.contextIds,
              };
            }
          } else {
            if (id === selectedVertex) {
              updatedSelectedVertexId = undefined;
            }
            delete currentNodes[id];
          }
        }
      }

      for (const index in linkIdsToDelete) {
        const linkId = linkIdsToDelete[index];
        let linkToDelete = currentLinks[linkId];
        if (linkToDelete) {
          if (Object.keys(linkToDelete.contextIds).length > 1) {
            delete linkToDelete.contextIds[contextId];
          } else {
            if (linkId === selectedEdgeId) {
              updatedSelectedEdgeId = undefined;
            }
            delete currentLinks[linkId];
          }
        }
      }

      // delete context
      delete updatedContexts[contextId];
      delete updatedContextIds[contextId];

      // Also clear suggestions that were sourced from this context
      const filteredSuggestedNodes = Object.fromEntries(
        Object.entries(state.suggestedNodes).filter(
          ([_, node]) => node.sourcePersonId !== contextId
        )
      );
      const filteredSuggestedEdges = Object.fromEntries(
        Object.entries(state.suggestedEdges).filter(
          ([_, edge]) => edge.sourcePersonId !== contextId
        )
      );

      return {
        ...state,
        nodes: currentNodes,
        links: currentLinks,
        contexts: updatedContexts,
        selectedVertex: updatedSelectedVertexId,
        selectedEdgeId: updatedSelectedEdgeId,
        contextIds: updatedContextIds,
        suggestedNodes: filteredSuggestedNodes,
        suggestedEdges: filteredSuggestedEdges,
        selectedSuggestedEdge: state.selectedSuggestedEdge && 
          filteredSuggestedEdges[state.selectedSuggestedEdge] ? state.selectedSuggestedEdge : null,
      };
    }
    case SET_NL_MESSAGE:
      return { ...state, aiContext: action.contexts };
    case REMOVE_NL_MESSAGE:
      const { contextId } = action;
      const { aiContext } = state;
      return {
        ...state,
        aiContext: aiContext.filter((context) => context.id !== contextId),
      };
    case SET_SELECTED_VERTEX:
      return {
        ...state,
        selectedVertex: action.vertex,
        selectedEdgeId: undefined,
      };
    case SET_SELECTED_LINK:
      // SET_SELECTED_LINK handled (log removed)
      if (state.selectedEdgeId === action.linkId && state.selectedVertex === undefined) {
        return state; // No change needed
      }
      return {
        ...state,
        selectedEdgeId: action.linkId,
        selectedVertex: undefined,
      };
    case SET_SEARCH_CONTEXT:
      return { ...state, searchContext: action.search };
    case UPDATE_SEARCH_CONTEXT_DISPLAY:
      return { ...state, searchContext: action.context };
    case SET_IS_LOADING:
      return { ...state, isLoading: action.value };
    case SET_TOP_CONNECTIONS_LOADING:
      return { ...state, topConnectionsLoading: action.value };
    case SET_TOP_CONNECTIONS:
      const newTopConnections = { ...state.topConnections };
      newTopConnections[action.id] = action.payload;
      return { ...state, topConnections: newTopConnections };
    case SET_MOST_CONNECTED_LOADING:
      return { ...state, mostConnectedLoading: action.value };
    case SET_MOST_CONNECTED:
      const newMostConnected = { ...state.mostConnected };
      newMostConnected[action.id] = action.payload;
      return { ...state, mostConnected: newMostConnected };
    case SET_FOCUSED_NODE: {
      // Store the id directly (not { id, ts }) so consumers reading
      // `focusedNode` get a stable string reference across same-id
      // dispatches — chart memos that depend only on the id don't
      // invalidate. F-05: also bump `focusPingTick` whenever a real id
      // is dispatched, so the focus useEffect (which depends on
      // [focusedNodeId, focusPingTick]) re-fires fit + ping on repeat
      // clicks of the same node.
      const nextId = action.nodeId ?? null;
      const sameId = state.focusedNode === nextId;
      const nextTick = nextId != null ? state.focusPingTick + 1 : state.focusPingTick;
      if (sameId && nextTick === state.focusPingTick) return state;
      return {
        ...state,
        focusedNode: sameId ? state.focusedNode : nextId,
        focusPingTick: nextTick,
      };
    }

    case SET_RELATIONSHIP_SUGGESTIONS: {
      perfMark('suggestions:reduce:start');
      perfCount('suggestions:set');
      const { sourcePersonId, suggestedNodes, suggestedEdges } = action.payload;
      // SET_RELATIONSHIP_SUGGESTIONS received (logs removed)

      // First, remove any existing suggestions from this same source
      const filteredNodes = Object.fromEntries(
        Object.entries(state.suggestedNodes).filter(
          ([_, node]) => node.sourcePersonId !== sourcePersonId
        )
      );
      const filteredEdges = Object.fromEntries(
        Object.entries(state.suggestedEdges).filter(
          ([_, edge]) => edge.sourcePersonId !== sourcePersonId
        )
      );
      
      // Add sourcePersonId to each new node and edge
      const taggedNodes = Object.fromEntries(
        Object.entries(suggestedNodes).map(([id, node]) => [
          id,
          { ...node, sourcePersonId },
        ])
      );
      const taggedEdges = Object.fromEntries(
        Object.entries(suggestedEdges).map(([id, edge]) => [
          id,
          { ...edge, sourcePersonId },
        ])
      );
      
      // Final merged suggestedNodes/Edges (logs removed)
      perfMeasure('suggestions:reduce', 'suggestions:reduce:start');
      return {
        ...state,
        suggestedNodes: { ...filteredNodes, ...taggedNodes },
        suggestedEdges: { ...filteredEdges, ...taggedEdges },
        suggestionsLoading: false,
      };
    }
    case SET_RELATIONSHIP_SUGGESTIONS_LOADING:
      return { ...state, suggestionsLoading: action.payload };
    case SET_ACTIVE_SUGGESTION_PANEL:
      return { ...state, activeSuggestionPanel: action.payload };
    case SET_SELECTED_SUGGESTED_EDGE:
      if (state.selectedSuggestedEdge === action.payload) {
        return state; // No change needed
      }
      return { ...state, selectedSuggestedEdge: action.payload };
    case DISMISS_RELATIONSHIP_SUGGESTION: {
      perfCount('suggestions:dismiss');
      const { personId1, personId2 } = action.payload;
      const edgeKey = [personId1, personId2].sort().join('--');
      const { [edgeKey]: removedEdge, ...remainingEdges } = state.suggestedEdges;
      
      const newDismissedList = [...state.dismissedSuggestions, edgeKey];
      const shouldClearSelection = state.selectedSuggestedEdge === edgeKey;

      // Early return if edge doesn't exist
      if (!removedEdge) {
        return {
          ...state,
          dismissedSuggestions: newDismissedList,
          selectedSuggestedEdge: shouldClearSelection ? null : state.selectedSuggestedEdge,
        };
      }
      
      // Determine the target person and check if they're still needed
      const targetPersonId = removedEdge.id1 === personId1 ? removedEdge.id2 : removedEdge.id1;
      const isPersonStillInUse = Object.values(remainingEdges).some(
        edge => edge.id1 === targetPersonId || edge.id2 === targetPersonId
      );

      const newSuggestedNodes = { ...state.suggestedNodes };
      if (!isPersonStillInUse) {
        delete newSuggestedNodes[targetPersonId];
      }

      return {
        ...state,
        suggestedEdges: remainingEdges,
        suggestedNodes: newSuggestedNodes,
        dismissedSuggestions: newDismissedList,
        selectedSuggestedEdge: shouldClearSelection ? null : state.selectedSuggestedEdge,
      };
    }
    case CLEAR_SUGGESTIONS:
      return {
        ...state,
        suggestedNodes: {},
        suggestedEdges: {},
        selectedSuggestedEdge: null,
      };
    case CLEAR_SUGGESTIONS_FOR_SOURCE: {
      perfCount('suggestions:clearForSource');
      const { sourcePersonId } = action.payload;
      const filteredNodes = Object.fromEntries(
        Object.entries(state.suggestedNodes).filter(
          ([_, node]) => node.sourcePersonId !== sourcePersonId
        )
      );
      const filteredEdges = Object.fromEntries(
        Object.entries(state.suggestedEdges).filter(
          ([_, edge]) => edge.sourcePersonId !== sourcePersonId
        )
      );
      return {
        ...state,
        suggestedNodes: filteredNodes,
        suggestedEdges: filteredEdges,
        selectedSuggestedEdge: state.selectedSuggestedEdge && 
          filteredEdges[state.selectedSuggestedEdge] ? state.selectedSuggestedEdge : null,
      };
    }

    case 'APP/GRAPH/SET_CONFIRMATION_RESULT':
      return { ...state, confirmationResult: action.payload };
    case RESET_COMPLETE_GRAPH:
      return initialState;
    default:
      return state;
  }
};

export default graphReducer;
